/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {useStreamTrackInfo} from '@app/components/voice/useStreamTrackInfo';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import VoiceSettingsStore from '@app/stores/VoiceSettingsStore';
import {isTrackReference, type TrackReferenceOrPlaceholder} from '@livekit/components-react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect} from 'react';

const containerStyle: React.CSSProperties = {
	position: 'absolute',
	top: '3.5rem',
	right: '0.75rem',
	zIndex: 40001,
	pointerEvents: 'none',
	padding: '4px 9px',
	borderRadius: '6px',
	background: 'rgba(0, 0, 0, 0.66)',
	color: '#fff',
	fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
	fontSize: '12px',
	fontWeight: 600,
	lineHeight: 1.4,
	letterSpacing: '0.02em',
	textShadow: '0 1px 2px rgba(0, 0, 0, 0.85)',
	whiteSpace: 'nowrap',
	userSelect: 'none',
};

function formatBitrate(kbps: number): string {
	if (!kbps || kbps <= 0) return '— kbps';
	return kbps >= 1000 ? `${(kbps / 1000).toFixed(1)} Mbps` : `${Math.round(kbps)} kbps`;
}

// Toggleable stats overlay (Ctrl+Shift+F). Self-managing: it registers the hotkey and reads the
// `showStreamStatsHud` setting itself, so the host only needs to render it with a track ref.
export const StreamStatsHud = observer(({trackRef}: {trackRef: TrackReferenceOrPlaceholder | null}) => {
	const show = VoiceSettingsStore.getShowStreamStatsHud();

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.ctrlKey && event.shiftKey && (event.code === 'KeyF' || event.key.toLowerCase() === 'f')) {
				event.preventDefault();
				VoiceSettingsStore.setShowStreamStatsHud(!VoiceSettingsStore.getShowStreamStatsHud());
			}
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);

	const info = useStreamTrackInfo(show ? trackRef : null);
	const stats = MediaEngineStore.voiceStats;

	if (!show) return null;

	const isLocal = Boolean(trackRef && isTrackReference(trackRef) && trackRef.participant?.isLocal);
	const bitrateKbps = isLocal ? stats.videoSendBitrate : stats.videoRecvBitrate;
	const resolution = info ? `${info.width}×${info.height}` : '—';
	const fps = info ? `${info.fps} fps` : '— fps';

	return (
		<div style={containerStyle} aria-hidden="true">
			{resolution} · {fps} · {formatBitrate(bitrateKbps)}
		</div>
	);
});
